import { Check, Plus, Upload, X } from 'lucide-react';
import { observer } from 'mobx-react';
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useNavigate, useParams } from 'react-router';
import { useWrapRequest } from 'use-wrap-request';
import { Button } from '@components/ui/button';
import { toast } from '@core/toast';
import { I18NText, ProductState, ProductSupportDto } from '@api/WalletClient';
import { Filters } from '../../components/filters';
import { useSearchReducer } from '../../components/hooks/useSearchReducer';
import { CellProps, TableComponent } from '../../components/table';
import { getCdnImage } from '../../components/utils/cdn-image';
import { Api } from '../../core/api';
import { links } from '../../core/router';
import { injectTSDI } from '../../core/tsdi';
import { ImageRequirements } from '../components/image-requirements';
import { StateCell } from '../components/state-cell';
import CreateProductModal from './create-product-modal';

type StateFilterType = 'ENABLED_FOR_USER' | 'POSSIBLE_TO_ENABLE' | 'ALL';

export const VendorProductList = observer(() => {
    const { walletClient } = injectTSDI(Api);
    const { vendorId } = useParams();
    const navigate = useNavigate();

    const [imageUploadLoading, setImageUploadLoading] = useState(false);
    const fileInputRef = React.useRef<HTMLInputElement>(null);

    const [searchParams, dispatch] = React.useReducer(useSearchReducer, {
        size: 30,
        page: 0
    });

    const [statusFilter, setStatusFilter] =
        React.useState<StateFilterType>('ALL');
    const [createOpen, setCreateOpen] = useState(false);
    const [bulkOperationLoading, setBulkOperationLoading] = useState(false);

    const bulkUpdateProducts = useWrapRequest(
        async (productIds: number[], targetState: ProductState) => {
            const promises = productIds.map((productId) =>
                walletClient.supportApi.vendorSupportControllerUpdateProductState(
                    Number(vendorId),
                    productId,
                    { state: targetState }
                )
            );
            await Promise.all(promises);
            return { success: true, updatedCount: productIds.length };
        },
        { defaultData: { success: false, updatedCount: 0 } }
    );

    const handleBulkEnable = async () => {
        const productIds = products.$?.content?.map(
            (p: ProductSupportDto) => p.id
        );

        if (!productIds || productIds.length === 0) {
            return;
        }

        setBulkOperationLoading(true);
        try {
            await bulkUpdateProducts.request([
                productIds,
                ProductState.PRODUCTION
            ]);
            dispatch({ type: 'reload' });
        } catch (error) {
            console.error('Bulk enable failed:', error);
            toast.error('Failed to enable products. Please try again.');
        } finally {
            setBulkOperationLoading(false);
        }
    };

    const handleBulkDisable = async () => {
        const productIds = products.$?.content?.map(
            (p: ProductSupportDto) => p.id
        );

        if (!productIds || productIds.length === 0) {
            return;
        }

        setBulkOperationLoading(true);
        try {
            await bulkUpdateProducts.request([
                productIds,
                ProductState.DISABLED
            ]);
            dispatch({ type: 'reload' });
        } catch (error) {
            console.error('Bulk disable failed:', error);
            toast.error('Failed to disable products. Please try again.');
        } finally {
            setBulkOperationLoading(false);
        }
    };

    const products = useWrapRequest(
        async (searchParams, stateFilter) => {
            const result =
                await walletClient.supportApi.vendorSupportControllerGetProductPage(
                    Number(vendorId),
                    {
                        ...searchParams,
                        ...(stateFilter === 'ENABLED_FOR_USER' && {
                            shopStates: [ProductState.PRODUCTION]
                        }),
                        ...(stateFilter === 'POSSIBLE_TO_ENABLE' && {
                            internalStates: [ProductState.DISABLED],
                            internalStatesIncludeNull: true,
                            externalStates: ProductState.PRODUCTION
                        }),
                        sort: 'value'
                    }
                );

            return { ...result.data, content: result.data.content || [] };
        },
        {
            defaultData: {
                content: [],
                number: 0,
                size: 10,
                totalElements: 0,
                totalPages: 0
            }
        }
    );

    const hasProducts = Boolean(products.$?.content?.length);

    React.useEffect(() => {
        products.request([searchParams, statusFilter]);
    }, [searchParams]);

    React.useEffect(() => {
        dispatch({ type: 'page', page: 0 });
    }, [statusFilter]);

    async function setState(item: ProductSupportDto, state: ProductState) {
        item.internalState = state;

        await walletClient.supportApi.vendorSupportControllerUpdateProductState(
            Number(vendorId),
            item.id,
            {
                state
            }
        );
        dispatch({ type: 'reload' });
    }

    const handleImageUpload = () => {
        fileInputRef.current?.click();
    };

    const handleFileChange = async (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        const file = event.target.files?.[0];
        if (!file) {
            return;
        }

        if (!file.type.startsWith('image/')) {
            toast.error('Please select an image file');
            return;
        }

        const maxSizeBytes = 5 * 1024 * 1024;
        if (file.size > maxSizeBytes) {
            toast.error('File size must be less than 5MB');
            return;
        }

        setImageUploadLoading(true);

        try {
            await walletClient.supportApi.vendorSupportControllerReplaceVendorProductImages(
                Number(vendorId),
                { file }
            );

            dispatch({ type: 'reload' });
        } catch (error) {
            console.error('Image upload failed:', error);
            toast.error('Failed to upload image. Please try again.');
        } finally {
            setImageUploadLoading(false);
            if (event.target) {
                event.target.value = '';
            }
        }
    };

    function toDisplayString(variant: I18NText | undefined) {
        const variantText = variant?.en;
        if (variantText) {
            return ` - variant ${variantText}`;
        }
        return '';
    }

    return (
        <>
            <input
                type="file"
                ref={fileInputRef}
                className="hidden"
                accept="image/*"
                onChange={handleFileChange}
            />

            <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                <h2 className="text-xl font-semibold tracking-tight">
                    Available products
                </h2>
                <div className="flex flex-wrap items-center gap-2">
                    <Button
                        loading={imageUploadLoading}
                        onClick={handleImageUpload}
                    >
                        <Upload />
                        {imageUploadLoading
                            ? 'Uploading...'
                            : 'Replace image in all products'}
                    </Button>
                    <Button
                        variant="outline"
                        onClick={() => setCreateOpen(true)}
                    >
                        <Plus />
                        Add product
                    </Button>
                    <Button
                        variant="outline"
                        className="text-success"
                        loading={bulkOperationLoading}
                        disabled={bulkOperationLoading || !hasProducts}
                        onClick={handleBulkEnable}
                    >
                        <Check />
                        {bulkOperationLoading
                            ? 'Processing...'
                            : 'Enable All Products'}
                    </Button>
                    <Button
                        variant="outline"
                        className="text-destructive"
                        loading={bulkOperationLoading}
                        disabled={bulkOperationLoading || !hasProducts}
                        onClick={handleBulkDisable}
                    >
                        <X />
                        {bulkOperationLoading
                            ? 'Processing...'
                            : 'Disable All Products'}
                    </Button>
                </div>
            </div>

            <div className="mb-4 flex flex-wrap items-end gap-3">
                <Filters<StateFilterType, false>
                    label="Status"
                    value={statusFilter}
                    width={180}
                    options={[
                        { label: 'all', value: 'ALL' },
                        {
                            label: 'enabled for users',
                            value: 'ENABLED_FOR_USER'
                        },
                        {
                            label: 'possible to enable',
                            value: 'POSSIBLE_TO_ENABLE'
                        }
                    ]}
                    onChange={(val) => void setStatusFilter(val)}
                />
                <div className="max-w-[500px] flex-1">
                    <ImageRequirements
                        aspectRatio="1:1 (e.g., 800×800, 1000×1000)"
                        minimumSize="400×400 pixels"
                    />
                </div>
            </div>

            <TableComponent<ProductSupportDto[]>
                emptyText="No entries"
                items={products}
                contextMenu={[
                    {
                        label: 'View Details',
                        action: (value) =>
                            navigate(
                                links.productDetails(Number(vendorId), value.id)
                            )
                    },
                    {
                        label: 'Disable',
                        hidden: (value) =>
                            value.internalState === ProductState.DISABLED,
                        action: (value) =>
                            setState(value, ProductState.DISABLED)
                    },
                    {
                        label: 'Enable for production',
                        hidden: (value) =>
                            value.internalState === ProductState.PRODUCTION,
                        action: (value) =>
                            setState(value, ProductState.PRODUCTION)
                    }
                ]}
                columns={[
                    {
                        Header: '',
                        accessor: 'image',
                        style: { width: 124 },
                        Cell: ({
                            row: {
                                original: { image, id }
                            }
                        }: CellProps<ProductSupportDto>) => (
                            <Link
                                to={links.productDetails(Number(vendorId), id)}
                            >
                                {image && (
                                    <img
                                        className="size-28 rounded-md object-cover"
                                        src={getCdnImage(
                                            image.bucketName,
                                            image.objectKey,
                                            {
                                                resize: {
                                                    width: 112,
                                                    height: 112
                                                }
                                            }
                                        )}
                                    />
                                )}
                            </Link>
                        )
                    },
                    {
                        Header: 'Prices',
                        accessor: 'prices',
                        Cell: ({
                            row: {
                                original: { prices, id }
                            }
                        }: CellProps<ProductSupportDto>) => (
                            <Link
                                to={links.productDetails(Number(vendorId), id)}
                                className="whitespace-pre-line"
                            >
                                {prices
                                    .map(
                                        (price) =>
                                            `${price.value.value} ${price.value.currency.code}${toDisplayString(price.variant)}`
                                    )
                                    .join('\n')}
                            </Link>
                        )
                    },
                    {
                        Header: 'Symbol',
                        accessor: (value) => `${value.symbol}`
                    },
                    {
                        Header: 'State',
                        Cell: ({
                            row: {
                                original: {
                                    internalState,
                                    externalState,
                                    shopState
                                }
                            }
                        }: CellProps<ProductSupportDto>) => (
                            <StateCell
                                internalState={internalState}
                                externalState={externalState}
                                shopState={shopState}
                            />
                        )
                    }
                ]}
            />
            <CreateProductModal
                open={createOpen}
                vendorId={Number(vendorId)}
                onClose={() => setCreateOpen(false)}
                onCreated={() => dispatch({ type: 'reload' })}
            />
        </>
    );
});

export default VendorProductList;
